import React, { useState } from 'react'
import { View } from 'react-native'
import { useSelector } from 'react-redux'
import { useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { StyleService, useStyleSheet } from '@src/style/service'
import { EhrAppointmentSchedulingInfo } from '@src/types'
import { CommonSelect } from '@src/components/CommonSelect'
import { AppStackParamList } from '@src/navigation/types'
import { Text } from '@src/components/base/Text'
import { Button } from '@src/components/base/Button'
import { CustomEventTypes } from '@src/config/analytics'
import {
  appointmentRecurrenceSelector,
  insurancePolicySelector,
} from '../../models/nutritionistHub.selectors'
import { useNutritionistHubTracker } from '../../hooks'

const RECURRING = 'recurring'
const ONE_TIME = 'one_time'
const VIDEO_CALL_RECURRING_OPTIONS = [
  { value: RECURRING, text: 'Recurring' },
  { value: ONE_TIME, text: 'One-time' },
]

interface ScheduleNextCallActionsProps {
  schedulingInfo: EhrAppointmentSchedulingInfo
}

export const ScheduleNextCallActions = ({ schedulingInfo }: ScheduleNextCallActionsProps) => {
  const styles = useStyleSheet(themedStyles)
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const track = useNutritionistHubTracker()

  const appointmentRecurrence = useSelector(appointmentRecurrenceSelector)
  const insurancePolicy = useSelector(insurancePolicySelector)

  const { bookingAvailable: canBookCall, options } = schedulingInfo
  const [selectedOption, setSelectedOption] = useState(() => options[0])
  const [recurringKind, setRecurringKind] = useState(() =>
    appointmentRecurrence && selectedOption.recurring ? RECURRING : ONE_TIME,
  )
  const selectOptions = options.map((option) => ({
    value: option.appointmentKind,
    text: `${option.duration} min`,
  }))
  const recurringAvailable = options.some((option) => option.recurring)

  const onSchedulePress = () => {
    track(CustomEventTypes.NutritionistHubScheduleNextCallTapped, {
      appointmentKind: selectedOption.appointmentKind,
      recurringKind,
      insurancePolicyEligible: insurancePolicy?.eligible,
    })

    if (recurringKind === RECURRING && insurancePolicy?.eligible) {
      navigation.navigate('UpsertRecurrence', { appointmentRecurrence, insurancePolicy })
      return
    }

    if (canBookCall) {
      navigation.navigate('CallScheduling', {
        appointmentKind: selectedOption.appointmentKind,
        pendingAppointmentId: schedulingInfo.pendingAppointment?.id,
        allowBackNavigation: true,
        product: selectedOption.product,
      })
    } else {
      navigation.navigate('VideoCallCheckout', { product: selectedOption.product || undefined })
    }
  }

  const onSelect = (option: any) => {
    const infoOption = options.find((someOption) => option.value === someOption.appointmentKind)
    if (infoOption) {
      setSelectedOption(infoOption)
    }
  }

  const onChangeRecurrence = (option: any) => {
    setRecurringKind(option.value)

    if (option.value !== RECURRING) {
      return
    }
    if (selectedOption.recurring) {
      return
    }

    // The currently selected option does not support recurring appointments;
    // change it to the first recurring option.
    const firstRecurringOption = options.find((option) => option.recurring)
    if (firstRecurringOption) {
      setSelectedOption(firstRecurringOption)
    }
  }

  return (
    <View style={styles.content}>
      {selectOptions.length > 1 ? (
        <CommonSelect
          selectedOption={selectOptions.find(
            (option) => option.value === selectedOption.appointmentKind,
          )}
          options={selectOptions}
          onSelect={onSelect}
          appearance="rounded"
          size="small"
          style={{ minWidth: 107 }}
          disabled={recurringKind === RECURRING}
        />
      ) : (
        <View style={styles.badge}>
          <Text type="small" style={styles.badge}>
            {selectOptions[0].text}
          </Text>
        </View>
      )}
      {recurringAvailable ? (
        <CommonSelect
          selectedOption={VIDEO_CALL_RECURRING_OPTIONS.find(
            (option) => option.value === recurringKind,
          )}
          options={VIDEO_CALL_RECURRING_OPTIONS}
          onSelect={onChangeRecurrence}
          appearance="rounded"
          size="small"
          style={{ minWidth: 130 }}
        />
      ) : (
        <View style={styles.badge}>
          <Text type="small" style={styles.badge}>
            One-time
          </Text>
        </View>
      )}
      <Button
        accessibilityLabel="Schedule Next Call"
        type="primary"
        size="small"
        onPress={onSchedulePress}
        style={styles.confirmButton}
      >
        Schedule
      </Button>
    </View>
  )
}

const themedStyles = StyleService.create({
  content: {
    marginTop: 12,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  confirmButton: {
    marginLeft: 'auto',
  },
  badge: {
    backgroundColor: 'theme.surface.base2',
    paddingHorizontal: 16,
    paddingVertical: 3,
    borderRadius: 32,
    textAlign: 'center',
  },
})
